import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api, API_URL, resolveMediaUrl } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

function ArtistsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const { user } = useAuth();
  const [artists, setArtists] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    name_ar: '',
    role: '',
    photo_url: null,
    worker_tier: 'both',
  });
  const [servicesModal, setServicesModal] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  const shopId = user?.shop_id;
  const branchId = user?.branch_id;
  const [selectedBranchId, setSelectedBranchId] = useState(branchId || null);
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);

  const fetchBranches = async () => {
    try {
      const data = await api.getManagerBranches();
      setBranches(data || []);
      if (data?.length > 0) {
        setCurrentBranch(data[0]);
        if (!selectedBranchId && !branchId) setSelectedBranchId(data[0].id);
        else if (branchId && !selectedBranchId) setSelectedBranchId(branchId);
      }
    } catch (err) { console.error(err); }
  };

  const fetchArtists = async () => {
    if (!selectedBranchId && !branchId) return;
    try { const data = await api.getManagerArtists(); setArtists(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBranches(); }, [shopId, branchId]);
  useEffect(() => { fetchArtists(); }, [selectedBranchId]);
  useEffect(() => { api.getManagerServices().then(setServices).catch(() => { }); }, []);

  const openServicesModal = async (artist) => {
    try {
      const res = await api.getManagerArtistServices(artist.id);
      setSelectedServiceIds(res.service_ids || []);
      setServicesModal(artist);
    } catch (err) { showAlert(err.message); }
  };

  const toggleService = (id) => setSelectedServiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const saveArtistServices = async () => {
    if (!servicesModal) return;
    try { await api.updateManagerArtistServices(servicesModal.id, { service_ids: selectedServiceIds }); setServicesModal(null); }
    catch (err) { showAlert(err.message || t('manager.pleaseSelectBranchFirst')); }
  };

  const openCreate = () => {
    if (!selectedBranchId && !branchId) { showAlert(t('manager.pleaseSelectBranchFirst')); return; }
    setEditing('create');
    setForm({ name: '', name_ar: '', role: '', photo_url: null, worker_tier: 'both' });
  };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      name: a.name,
      name_ar: a.name_ar || '',
      role: a.role,
      photo_url: a.photo_url || null,
      worker_tier: a.worker_tier || 'both',
    });
  };

  const uploadPhoto = async (e) => {
    if (!editing || editing === 'create' || !e.target?.files?.[0]) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) { showAlert(t('manager.pleaseSelectImageFile')); return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const updated = await api.uploadManagerArtistPhoto(editing, fd);
      setForm(f => ({ ...f, photo_url: updated.photo_url }));
      fetchArtists();
    } catch (err) { showAlert(err.message || t('manager.pleaseSelectBranchFirst')); }
    e.target.value = '';
  };

  const deletePhoto = async () => {
    if (!editing || editing === 'create' || !form.photo_url) return;
    showConfirm(t('admin.deletePhotoConfirm', 'Delete this photo?'), async () => {
      try {
        const updated = await api.deleteManagerArtistPhoto(editing);
        setForm(f => ({ ...f, photo_url: updated.photo_url ?? null }));
        fetchArtists();
      } catch (err) { showAlert(err.message); }
    });
  };

  const save = async () => {
    const targetBranchId = selectedBranchId || branchId;
    if (!targetBranchId) { showAlert(t('manager.branchIdRequired')); return; }
    try {
      const payload = {
        branch_id: targetBranchId,
        name: form.name,
        name_ar: form.name_ar || null,
        role: form.role,
        worker_tier: form.worker_tier || 'both',
      };
      if (editing === 'create') await api.createManagerArtist(payload);
      else await api.updateManagerArtist(editing, payload);
      setEditing(null); fetchArtists();
    } catch (err) { showAlert(err.message || t('manager.pleaseSelectBranchFirst')); }
  };

  const remove = async (id) => {
    showConfirm(t('manager.deleteWorkerConfirm'), async () => {
      try { await api.deleteManagerArtist(id); fetchArtists(); }
      catch (err) { showAlert(err.message || t('manager.pleaseSelectBranchFirst')); }
    });
  };

  const isShopManager = user?.role === 'shop_manager' || user?.role === 'salon_manager';

  return (
    <AdminLayout role="manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('manager.workers')}</h1>
          {selectedBranchId && (
            <p style={{ fontSize: '0.82rem', color: T.muted, marginTop: '0.3rem' }}>
              {t('manager.branchLabel')} <span style={{ color: T.white, fontWeight: 600 }}>{branches.find(b => b.id === selectedBranchId)?.name || t('common.loading')}</span>
            </p>
          )}
          <div style={{ width: '40px', height: '2px', background: T.orange, marginTop: '0.5rem' }} />
        </div>
        {selectedBranchId && (
          <button style={S.btnOrange} onClick={openCreate}
            onMouseEnter={e => e.target.style.background = T.orangeHov}
            onMouseLeave={e => e.target.style.background = T.orange}>{t('manager.addWorker')}</button>
        )}
      </div>

      {isShopManager && branches.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.selectBranch')}</label>
          <select value={selectedBranchId || ''} onChange={e => setSelectedBranchId(parseInt(e.target.value))} style={{ ...S.select, width: 'auto', marginBottom: 0 }}>
            <option value="">{t('manager.selectBranchOption')}</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      {!selectedBranchId && !branchId && <p style={{ color: T.muted }}>{t('manager.pleaseSelectBranchToViewWorkers')}</p>}

      {(selectedBranchId || branchId) && (
        <>
          {editing && (
            <div style={S.modal}>
              <div style={S.modalBox}>
                <h2 style={S.modalTitle}>{editing === 'create' ? t('manager.newWorker') : t('manager.editWorker')}</h2>
                {editing !== 'create' && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.photo')}</p>
                    {(form.photo_url) ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <img src={resolveMediaUrl(form.photo_url)} alt="" loading="lazy" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.border || 'var(--border)'}` }} />
                        <label style={{ cursor: 'pointer', fontSize: '0.85rem', color: T.orange, textDecoration: 'underline' }}>
                          {t('manager.changePhoto')}
                          <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                        </label>
                        <button type="button" onClick={deletePhoto} style={{ ...S.btnDanger, padding: '0.35rem 0.6rem', fontSize: '0.75rem' }}>
                          {t('common.delete')}
                        </button>
                      </div>
                    ) : (
                      <label style={{ display: 'inline-block', fontSize: '0.85rem', color: T.orange, cursor: 'pointer', textDecoration: 'underline' }}>
                        {t('manager.uploadPhoto')}
                        <input type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                      </label>
                    )}
                  </div>
                )}
                <input placeholder={t('manager.placeholderName')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
                <input placeholder={t('manager.placeholderNameAr')} value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} style={S.input} dir="rtl" />
                <input placeholder={t('admin.role')} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={S.input} />
                <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('manager.workerTier')}</label>
                <select
                  value={form.worker_tier}
                  onChange={e => setForm({ ...form, worker_tier: e.target.value })}
                  style={{ ...S.select, width: '100%', marginBottom: '1rem' }}
                >
                  <option value="both">{t('manager.workerTierBoth')}</option>
                  <option value="basic">{t('manager.workerTierBasic')}</option>
                  <option value="vip">{t('manager.workerTierVip')}</option>
                </select>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
                  <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {servicesModal && (
            <div style={S.modal}>
              <div style={{ ...S.modalBox, maxHeight: '80vh', overflowY: 'auto' }}>
                <h2 style={S.modalTitle}>{t('manager.servicesForName', { name: servicesModal.name })}</h2>
                <p style={{ fontSize: '0.82rem', color: T.muted, marginBottom: '1rem' }}>{t('manager.selectServicesWorkerCanPerform')}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
                  {services.length === 0 ? <p style={{ color: T.muted }}>{t('manager.noServicesAddFirst')}</p> : (
                    services.map(svc => (
                      <label key={svc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', color: T.white, fontSize: '0.88rem' }}>
                        <input type="checkbox" checked={selectedServiceIds.includes(svc.id)} onChange={() => toggleService(svc.id)}
                          style={{ accentColor: T.orange }} />
                        {svc.name} ({svc.duration_minutes} min, {svc.price} EGP)
                      </label>
                    ))
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={saveArtistServices} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
                  <button onClick={() => setServicesModal(null)} style={S.btnOutline}>{t('common.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>{[t('manager.tablePhoto'), t('manager.tableId'), t('manager.tableName'), t('manager.tableBranch'), t('manager.tableRole'), t('manager.tableWorkerTier'), t('manager.tableRating'), t('manager.tableActions')].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {artists
                    .filter(a => !selectedBranchId || a.branch_id === selectedBranchId)
                    .map(a => (
                      <tr key={a.id}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={S.td}>
                          {a.photo_url ? (
                            <img src={resolveMediaUrl(a.photo_url)} alt="" loading="lazy" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: `1px solid ${T.border || 'var(--border)'}` }} />
                          ) : (
                            <span style={{ color: T.muted, fontSize: '0.75rem' }}>—</span>
                          )}
                        </td>
                        <td style={S.td}>{a.id}</td>
                        <td style={S.td}>{a.name}</td>
                        <td style={S.td}>{currentBranch?.name || branches.find(b => b.id === a.branch_id)?.name || `Branch ${a.branch_id}`}</td>
                        <td style={S.td}>{a.role}</td>
                        <td style={S.td}>
                          {a.worker_tier === 'vip' ? t('manager.workerTierVip') : a.worker_tier === 'basic' ? t('manager.workerTierBasic') : t('manager.workerTierBoth')}
                        </td>
                        <td style={{ ...S.td, color: T.orange }}>★ {a.rating}</td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button style={S.btnSmall} onClick={() => openEdit(a)}
                              onMouseEnter={e => e.target.style.color = T.white}
                              onMouseLeave={e => e.target.style.color = T.muted}>{t('manager.edit')}</button>
                            <button style={S.btnSmall} onClick={() => openServicesModal(a)}
                              onMouseEnter={e => e.target.style.color = T.orange}
                              onMouseLeave={e => e.target.style.color = T.muted}>{t('manager.services')}</button>
                            <button style={S.btnDanger} onClick={() => remove(a.id)}>{t('manager.delete')}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

export default function Artists() {
  return <ProtectedRoute requireSalonManager><ArtistsContent /></ProtectedRoute>;
}
